<template>
	<div class="divide-y divide-gray-100 border-b border-gray-100">
		<v-page-product-details-group
			v-if="description || (qaItems && qaItems.length > 0)"
			:title="`${t('product-description')}`"
		>
			<!-- eslint-disable vue/no-v-html -->
			<div v-if="description" class="prose" v-html="description"></div>

			<div v-if="qaItems && qaItems.length > 0" class="mt-md space-y-md">
				<div v-for="(qa, index) in qaItems" :key="index" class="">
					<div class="prose" v-html="toSafeHtml(qa.question)"></div>

					<div class="prose" v-html="toSafeHtml(qa.answer)"></div>
				</div>
			</div>
		</v-page-product-details-group>

		<v-page-product-details-group
			v-for="group in groups"
			:key="group.id"
			:title="group.title"
			:is-open="group.title === t('dimensions')"
		>
			<dl
				v-for="attribute in group.attributes"
				:key="attribute.id"
				class="flex justify-between space-x-md p-sm odd:bg-gray-50"
			>
				<dt class="whitespace-nowrap">{{ attribute.title }}</dt>

				<dd class="text-right font-bold">{{ attribute.value }}</dd>
			</dl>
		</v-page-product-details-group>

		<v-page-product-details-group
			v-if="firstMeasurementAsset"
			:title="`${t('measurement')}`"
			scrollable
		>
			<div>
				<nuxt-picture
					:src="firstMeasurementAsset.url"
					:alt="firstMeasurementAsset.title ?? ''"
					class="w-full"
					width="100%"
					height="auto"
				/>
			</div>
		</v-page-product-details-group>

		<v-page-product-details-group
			v-if="regularAssets.length > 0"
			:title="`${t('assets')}`"
			scrollable
		>
			<ul v-for="asset in regularAssets" :key="asset.id" :asset="asset">
				<li>
					<a
						:href="asset.url"
						class="text-black hover:underline"
						target="_blank"
						rel="noopener noreferrer"
					>
						{{ asset.title || getFilename(asset.url) }}
					</a>
				</li>
			</ul>
		</v-page-product-details-group>

		<v-page-product-details-group
			v-if="isParcelsVisible && parcels.length > 0"
			:title="`${t('parcel-title')}`"
			scrollable
		>
			<v-page-product-details-parcels :parcels="parcels" />
		</v-page-product-details-group>
	</div>
</template>

<script lang="ts" setup>
import sanitize from "sanitize-html";
import {useAttributeValue} from "@pkg/shared/composables/use-attribute-value";
import {
	type ViewProductDetailsQuery,
	type ViewProductQuery,
	type QuestionAndAnswer,
} from "@pkg/shared/graphql";

import type {ProductKind} from "@pkg/shared/types";

type DetailProduct = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;
type AttributeGroup = NonNullable<
	DetailProduct["attributes"][0]["attribute"]["group"]
>;
type ProductAttributeValue = DetailProduct["attributes"][0];
type ProductParcel = DetailProduct["parcels"][0];
type ProductDimensions = DetailProduct["dimensions"];
type ProductAssets = ProductKind<ViewProductQuery["product"]>["assets"];

interface AttributeData {
	id: string;
	title: string;
	value?: string;
}

interface AttributeGroupData {
	id: string;
	title: string;
	attributes: AttributeData[];
	position?: number;
}

const properties = defineProps<{
	description?: string;
	attributeValues: ProductAttributeValue[];
	parcels: ProductParcel[];
	dimensions?: ProductDimensions;
	sku?: string;
	lowestPrice?: string;
	assets: ProductAssets;
	hidePriceHistory?: boolean;
	isParcelsVisible?: boolean;
	qaItems?: QuestionAndAnswer[] | null;
}>();

const {t} = useI18n();

// https://www.npmjs.com/package/sanitize-html#default-options
const toSafeHtml = (html: string) =>
	sanitize(html, {
		// NOTE: Ensure allowedTags are synced with backoffice!
		allowedTags: [
			"address",
			"article",
			"aside",
			"footer",
			"header",
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"hgroup",
			"main",
			"nav",
			"section",
			"blockquote",
			"dd",
			"div",
			"dl",
			"dt",
			"figcaption",
			"figure",
			"hr",
			"li",
			"main",
			"ol",
			"p",
			"pre",
			"ul",
			"a",
			"abbr",
			"b",
			"bdi",
			"bdo",
			"br",
			"cite",
			"code",
			"data",
			"dfn",
			"em",
			"i",
			"kbd",
			"mark",
			"q",
			"rb",
			"rp",
			"rt",
			"rtc",
			"ruby",
			"s",
			"samp",
			"small",
			"span",
			"strong",
			"sub",
			"sup",
			"time",
			"u",
			"var",
			"wbr",
			"caption",
			"col",
			"colgroup",
			"table",
			"tbody",
			"td",
			"tfoot",
			"th",
			"thead",
			"tr",
		],
		allowedAttributes: {},
		nonBooleanAttributes: [],
	});

const productDimensions = computed<Partial<ProductDimensions>>(() => {
	if (properties.dimensions) {
		return {
			width: properties.dimensions.width,
			diameter: properties.dimensions.diameter,
			depth: properties.dimensions.depth,
			height: properties.dimensions.height,
			weight: properties.dimensions.weight,
			volume: properties.dimensions.volume,
		};
	}
	return {};
});

const attributesData = computed<AttributeData[]>(() =>
	properties.attributeValues.map((attributeValue) =>
		useAttributeValue(attributeValue),
	),
);

const groups = computed<AttributeGroupData[]>(() => {
	const data = properties.attributeValues
		.map((attributeValue) => attributeValue.attribute.group)
		.filter((group): group is AttributeGroup => !!group)
		.reduce<AttributeGroupData[]>(
			(accumulator, group) =>
				accumulator.some((item) => item.id === group.id)
					? accumulator
					: [
							...accumulator,
							{
								id: group.id,
								title: group.title,
								description: group.description,
								position: group.position,
								attributes: properties.attributeValues
									.filter(
										(attributeValue) =>
											attributeValue.attribute.group?.id === group.id,
									)
									.map((attributeValue) => {
										const attribute = attributesData.value.find(
											(item) => item.id === attributeValue.attribute.id,
										);

										return {
											id: attribute?.id || "",
											title: attribute?.title || "",
											value: attribute?.value,
										};
									})
									.filter((attributeData) => attributeData.id.length > 0),
							},
						],
			[],
		)
		.sort((a, b) => (a.position || 0) - (b.position || 0));

	if (properties.sku) {
		let additionalDetails: AttributeGroupData | undefined = data.find(
			(item) => item.title === t("product-info"),
		);

		if (!additionalDetails) {
			additionalDetails = {
				id: "info",
				title: t("product-info"),
				attributes: [],
			};

			data.unshift(additionalDetails);
		}

		additionalDetails.attributes = [
			{
				id: properties.sku,
				title: t("product-sku"),
				value: properties.sku,
			},
			...additionalDetails.attributes,
		];
	}

	if (Object.entries(productDimensions.value).length > 0) {
		let dimensionsGroup: AttributeGroupData | undefined = data.find(
			(item) => item.title === t("dimensions"),
		);

		if (!dimensionsGroup) {
			dimensionsGroup = {
				id: "dimensions",
				title: t("dimensions"),
				attributes: [],
			};

			data.unshift(dimensionsGroup);
		}

		const dimensionsAttributeData: AttributeData[] = [];

		const filledDimensions = Object.entries(productDimensions.value).filter(
			([, value]) => !!value,
		);

		for (const [key, value] of filledDimensions) {
			dimensionsAttributeData.push({
				id: key,
				title: t(`product-dimensions.${key}`),
				value: `${value} ${t(`dimension-suffix.${key}`)}`,
			});
		}

		dimensionsGroup.attributes = [
			...dimensionsAttributeData,
			...dimensionsGroup.attributes,
		];
	}

	return data;
});

const measurementAssets = computed(() =>
	properties.assets.filter((asset) => asset.isMeasurement),
);
const firstMeasurementAsset = computed(() => measurementAssets.value[0]);
const regularAssets = computed(() =>
	properties.assets.filter((asset) => !asset.isMeasurement),
);

function getFilename(url: string): string {
	const parts = url.split("/");

	return parts.at(-1) || "";
}
</script>

<i18n lang="json">
{
	"et": {
		"product-description": "Toote kirjeldus",
		"product-sku": "Tootekood",
		"product-info": "Lisainfo",
		"dimensions": "Mõõdud",
		"assets": "Juhised ja brošüürid",
		"measurement": "Mõõtjoonis",
		"dimension-suffix": {
			"height": "cm",
			"width": "cm",
			"depth": "cm",
			"diameter": "cm",
			"weight": "kg",
			"volume": "cl"
		},
		"product-dimensions": {
			"height": "Kõrgus",
			"width": "Laius",
			"weight": "Kaal",
			"depth": "Sügavus",
			"diameter": "Läbimõõt",
			"volume": "Maht"
		},
		"parcel-title": "Pakendi teave",
		"parcel": {
			"quantity-suffix": "tk",
			"quantity": "Pakke",
			"products": "Tooteid",
			"height": "Kõrgus",
			"length": "Pikkus",
			"width": "Laius",
			"volume": "Maht",
			"weight": "Kaal"
		},
		"lowest-price-in-the-last-30-days": "30 päeva madalaim hind"
	},
	"fi": {
		"product-sku": "Tuotekoodi",
		"product-info": "Lisätiedot",
		"product-description": "Tuotekuvaus",
		"dimensions": "Mitat",
		"parcel-title": "Pakkaustiedot",
		"assets": "Ohjeet ja esitteet",
		"measurement": "Mittakuva",
		"dimension-suffix": {
			"height": "cm",
			"width": "cm",
			"depth": "cm",
			"diameter": "cm",
			"weight": "kg",
			"volume": "cl"
		},
		"product-dimensions": {
			"height": "Korkeus",
			"width": "Leveys",
			"weight": "Paino",
			"depth": "Syvyys",
			"diameter": "Halkaisija",
			"volume": "Nesteen tilavuus"
		},
		"parcel": {
			"quantity-suffix": "kpl",
			"quantity": "Paketteja",
			"products": "Tuotteita",
			"height": "Korkeus",
			"length": "Pituus",
			"width": "Leveys",
			"volume": "Tilavuus",
			"weight": "Paino"
		},
		"lowest-price-in-the-last-30-days": "Alin hinta viim. 30 pv aikana"
	}
}
</i18n>
